import { SearchableList, TooltipedTextComponent } from 'commons/components';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { sortAndJoin } from 'commons/utils/sort.utils';
import { useTranslation } from 'core/i18n/i18n';
import { useMemo } from 'react';
import { USER_LOCATION_ASSIGNMENT_I18N_PREFIX } from 'modules/settings/pods/settings-user-location-assignment/settings-user-location-assignment.constants';
import { L1Location } from 'modules/settings/pods/settings-user-location-assignment/service/user-locations/hooks/use-l1-locations.hook';
import { NO_RESTRICTED_LOCATIONS_ID } from './restrict-locations.constants';
import { RestrictLocationsContent, RestrictLocationsDialog } from './restrict-locations.style';

interface RestrictLocationsComponentProps {
    isOpen: boolean;
    userNames: string[];
    locations: L1Location[];
    selectedIds: string[];
    isSelectionKnown: boolean;
    isLoading: boolean;
    isError: boolean;
    isSaving: boolean;
    canUpdate: boolean;
    onSelectionChange: (ids: string[]) => void;
    onClearAll: VoidFunction;
    onSelectAll: VoidFunction;
    onUpdate: VoidFunction;
    onCancel: VoidFunction;
}

export const RestrictLocationsComponent: React.FC<RestrictLocationsComponentProps> = ({
    isOpen,
    userNames,
    locations,
    selectedIds,
    isSelectionKnown,
    isLoading,
    isError,
    isSaving,
    canUpdate,
    onSelectionChange,
    onClearAll,
    onSelectAll,
    onUpdate,
    onCancel,
}) => {
    const { t } = useTranslation();

    // "No restricted locations" is the visible form of a decided empty set, so it is checked exactly
    // when nothing else is — the two can never be selected together, and undecided checks neither.
    const fixedItems = useMemo(
        () => [{ id: NO_RESTRICTED_LOCATIONS_ID, name: t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.locations.none`) }],
        [t]
    );
    const listSelection = isSelectionKnown && selectedIds.length === 0 ? [NO_RESTRICTED_LOCATIONS_ID] : selectedIds;

    const handleSelectItem = (ids: (string | number)[]) => {
        const asStrings = ids.map(String);
        const clearedExplicitly = asStrings.includes(NO_RESTRICTED_LOCATIONS_ID) && selectedIds.length > 0;

        onSelectionChange(clearedExplicitly ? [] : asStrings.filter((id) => id !== NO_RESTRICTED_LOCATIONS_ID));
    };

    return (
        <RestrictLocationsDialog
            isOpen={isOpen}
            title={
                userNames.length === 1 ? (
                    t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.title`, { name: userNames[0] })
                ) : (
                    <TooltipedTextComponent
                        i18nKey={`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.title-multiple`}
                        tooltipText={sortAndJoin(userNames)}
                        i18nValues={{ count: userNames.length }}
                        tooltipAnchorClassname="blue-tooltip-span"
                        showTooltipAlways
                    />
                )
            }
            loading={isLoading}
            onClose={onCancel}
            prefixFooter={
                selectedIds.length ? (
                    <PocButton
                        variant="tertiary"
                        size="medium"
                        disabled={isSaving}
                        onClick={onClearAll}
                        data-testid="restrict-locations-select-toggle"
                    >
                        {t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.clear-all`)}
                    </PocButton>
                ) : (
                    <PocButton
                        variant="tertiary"
                        size="medium"
                        disabled={isSaving || locations.length === 0}
                        onClick={onSelectAll}
                        data-testid="restrict-locations-select-toggle"
                    >
                        {t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.select-all`)}
                    </PocButton>
                )
            }
            // ModalDialog renders its own Cancel in the footer suffix; footerOptions is appended after
            // it, so this must carry the primary action only or the dialog shows two Cancels.
            cancelButtonDisabled={isSaving}
            footerOptions={
                <PocButton
                    variant="primary"
                    size="medium"
                    disabled={!canUpdate}
                    onClick={onUpdate}
                    data-testid="restrict-locations-update"
                >
                    {t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.update`, {
                        count: selectedIds.length,
                    })}
                </PocButton>
            }
        >
            <RestrictLocationsContent>
                {isError ? (
                    <PocTypography variant="body2" data-testid="restrict-locations-error">
                        {t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.load-error`)}
                    </PocTypography>
                ) : (
                    <SearchableList
                        items={locations}
                        fixedItems={fixedItems}
                        multiple
                        selectedIds={listSelection}
                        onSelectItem={handleSelectItem}
                        messageNoItems={t(`${USER_LOCATION_ASSIGNMENT_I18N_PREFIX}.restrict-locations.no-matches`)}
                    />
                )}
            </RestrictLocationsContent>
        </RestrictLocationsDialog>
    );
};
